/**
 * Utils to process EPUB file
 */

// `jszip` is for unzip the epub file
import JSZip from "jszip";
// `xml2js` is to read content from the unzipped epub file
import xml2js from "xml2js";
import {newDecodeBase64ToArrayBuffer, newEncodeArrayBufferBase64} from "./s2k-encodeArrayBufferBase64";
import {EPUB_HELPER} from "../constants";

// ############################################# Public #############################################
/**
 * Extracting the content(title, author and html content) from an epub based on a given base64 string of an epub file
 * @param {String} epubString The base64 string of an epub file
 * @return {Promise<{title: string, author: string, content: string}>} Return the title, author and html content as an object
 */
async function extractMetadataFromEpubString(epubString) {
    let unzippedEpub = await getUnzippedEpub(epubString);
    // Get title and author
    let epubMetadata = await extractEpubMetadata(unzippedEpub);
    // Get html content
    let content = await extractEpubContent(unzippedEpub);
    return {
        title: epubMetadata.title,
        author: epubMetadata.author,
        content: content
    };
}

/**
 * Get the unzipped epub content from a base64 string of an epub file
 * @param {String} epubString The base64 string of an epub file
 * @return {Promise<JSZip>} A jszip object with unzipped epub content
 */
async function getUnzippedEpub(epubString) {
    try {
        let epubFile = newDecodeBase64ToArrayBuffer(epubString);
        let unzippedEpub = new JSZip();
        await unzippedEpub.loadAsync(epubFile);
        return unzippedEpub;
    } catch (error) {
        return undefined;
    }
}

/**
 * Update title and author in epub metadata and epub content,
 * then write the updated content and metadata file back to the EPUB
 * then return a Base64 of updated EPUB
 * @param {String} epubString Base64 string of original EPUB
 * @param {String} newTitle New title
 * @param {String} newAuthor New author
 * @return {Promise<String>} Base64 string of new EPUB
 */
async function updateEpubMetadata(epubString, newTitle, newAuthor) {
    let unzippedEpub = await getUnzippedEpub(epubString);
    if (!unzippedEpub) {
        // If the epub cannot unzip, return the original base64 string
        return epubString;
    }
    // Builder is to build xml file of content/metadata after updated title and author.
    let builder = new xml2js.Builder();

    // Update metadata in opf file
    let opfFile = unzippedEpub.file(EPUB_HELPER.METADATA_FILE_PATH);
    if (opfFile) {
        // Get pure string from opf file
        let content = await opfFile.async('text');
        // Parse the pure string to xml object
        let result = await xml2js.parseStringPromise(content);
        if (result) {
            // Get title from EPUB 3.0, the path is ["opf:package"]["opf:metadata"][0]["dc:title"]
            if (result["opf:package"]["opf:metadata"][0]["dc:title"]) {
                result["opf:package"]["opf:metadata"][0]["dc:title"][0] = newTitle;
            }
            // Update author from EPUB 3.0, the path is ["opf:package"]["opf:metadata"][0]["dc:creator"][0]["_"],
            // in 'xml2js' the result of ["opf:package"]["opf:metadata"][0]["dc:creator"][0] is an object, its value is in key "_"
            if (result["opf:package"]["opf:metadata"][0]["dc:creator"] && result["opf:package"]["opf:metadata"][0]["dc:creator"][0]) {
                result["opf:package"]["opf:metadata"][0]["dc:creator"][0]._= newAuthor;
            }
            // Generate XML of updated info
            let modifiedContentOpf = builder.buildObject(result);
            // Write new XML back to opf file in unzipped epub file
            unzippedEpub.file(EPUB_HELPER.METADATA_FILE_PATH, modifiedContentOpf);
        }
    }

    // Update title and author in html content
    let htmlContent = unzippedEpub.file(EPUB_HELPER.HTML_CONTENT_PATH);
    if (htmlContent) {
        // Get pure string of html content
        let htmlString = await htmlContent.async('text');
        // Convert string to DOM
        let dom = getDOMFromHtmlString(htmlString);
        // Update title div
        let titleDiv = dom.querySelector('.s2k-title');
        if (titleDiv) {
            titleDiv.textContent = newTitle;
        }
        // Update author div
        let authorDiv = dom.querySelector('.s2k-source-short');
        if (authorDiv) {
            authorDiv.textContent = newAuthor;
        }
        // Convert updated DOM to String
        let modifiedContent = new XMLSerializer().serializeToString(dom);
        // Write updated string to `content.xhtml` file
        unzippedEpub.file(EPUB_HELPER.HTML_CONTENT_PATH, modifiedContent);
    }
    // Generate Base64 encoded string and return
    return await getEpubStringFromEpubFile(unzippedEpub);
}

// ############################################# Private #############################################

/**
 * Encode an unzipped epub file to base64 string
 * @param {JSZip} unzippedEpub Unzipped epub file
 * @return {Promise<string>} Base64 encoded string
 */
async function getEpubStringFromEpubFile(unzippedEpub) {
    let epubData = await unzippedEpub.generateAsync({type: 'arraybuffer'});
    return newEncodeArrayBufferBase64(epubData);
}

/**
 * Get Pure text of html content from an unzipped epub file
 * @param {JSZip} unzippedEpub The unzipped epub file
 * @return {Promise<string | undefined>} Pure text of html content of the given unzipped epub
 */
async function extractEpubContent(unzippedEpub) {
    try {
        let contentFile = unzippedEpub.file(EPUB_HELPER.HTML_CONTENT_PATH);
        if (contentFile) {
            // Get pure string of html content
            let htmlString = await contentFile.async('text');
            return removeDupInfoFromContent(htmlString);
        } else {
            return undefined;
        }
    } catch (e) {
        // If any exception occur when extract metadata, return undefined as the content
        return undefined;
    }
}

/**
 * Get Title and author from an unzipped epub file
 * @param unzippedEpub unzipped epub file
 * @return {Promise<{title: string, author: string}>} Title and author of the given unzipped epub
 */
async function extractEpubMetadata(unzippedEpub) {
    try{
        const opfFile = unzippedEpub.file(EPUB_HELPER.METADATA_FILE_PATH);
        if (opfFile) {
            let content = await opfFile.async('text');
            let result = await xml2js.parseStringPromise(content);
            if (result) {
                let title = "";
                let author = "";
                // Get title from EPUB 3.0, the path is ["opf:package"]["opf:metadata"][0]["dc:title"]
                if (result["opf:package"]["opf:metadata"][0]["dc:title"]) {
                    title = result["opf:package"]["opf:metadata"][0]["dc:title"][0] || "";
                }
                // Get author from EPUB 3.0, the path is ["opf:package"]["opf:metadata"][0]["dc:creator"][0]._,
                // in 'xml2js' the result of ["opf:package"]["opf:metadata"][0]["dc:creator"][0] is an object, its value is in key "_"
                if (result["opf:package"]["opf:metadata"][0]["dc:creator"] && result["opf:package"]["opf:metadata"][0]["dc:creator"][0]) {
                    author = result["opf:package"]["opf:metadata"][0]["dc:creator"][0]._ || "";
                }
                // Treat all space title or author as undefined
                title = title.trim() === "" ? undefined : title.trim();
                author = author.trim() === "" ? undefined : author.trim();
                return {title: title, author: author};
            }
        }
        return {title: undefined, author: undefined};
    } catch (e) {
        // If any exception occur when extract metadata, set title and author to undefined
        return {title: undefined, author: undefined};
    }
}

/**
 * Get html String form a DOM
 * @param {Document} dom A DOM
 * @return {String} The HTML string of the given DOM
 */
function getHtmlStringFromDOM(dom) {
    let htmlString;
    try {
        htmlString = new XMLSerializer().serializeToString(dom);
    } catch (e) {
        htmlString = "";
    }
    return htmlString;
}

/**
 * Get DOM based on a html String
 * @param {String} htmlString HTML String
 * @return {Document} The DOM of the given HTML String
 */
function getDOMFromHtmlString(htmlString) {
    let parser = new DOMParser();
    return parser.parseFromString(htmlString, 'text/html');
}


/**
 * Since the content of EPUB contains Header part(title and author) and Footer part(source),
 * which are duplicated with preview template. So that need to be removed
 * @param {String} htmlString String of HTML content that need to be de-dup
 * @return {string} The De-duped String of the given HTML String
 */
function removeDupInfoFromContent(htmlString) {
    // Get the DOM from the given HTML String
    let dom = getDOMFromHtmlString(htmlString);
    // Remove header
    let header = dom.querySelector(".s2k-article-header");
    header.parentNode.removeChild(header);
    // Remove footer
    let footer = dom.querySelector(".s2k-article-footer");
    footer.parentNode.removeChild(footer);
    return getHtmlStringFromDOM(dom);
}

export {
    extractMetadataFromEpubString,
    getUnzippedEpub,
    updateEpubMetadata,
    getDOMFromHtmlString
};